import { appendQueryToShopifyImageUrl } from "../global/utils/append-query-to-shopify-image-url";
import Choices from "choices.js";

const selectors = {
    container: '.js-product-bundle',
    option: '[data-product-bundle-option]',
    item: '[data-bundle-product-item]',
    itemImage: '[data-bundle-product-item-image]',
    itemOptions: '[data-product-bundle-item-options]',
    itemColorOption: '[data-bundle-product-item-color-option]',
    itemOption: '[data-bundle-product-item-option]',
    itemQuantityWrapper: '[data-bundle-product-item-quantity-wrapper]',
    itemQuantity: '[data-bundle-product-item-quantity]',
    itemQuantityButton: '[data-bundle-product-item-quantity-button]',
    itemSelect: '[data-bundle-product-item-select]',
    submit: '.js-product-bundle-submit',
    totalPrice: '[data-bundle-total-price]',
    form: '.js-product-bundle-form',
    JSON: '[data-product-bundle-json]'
};

const modifiers = {
    hidden: 'hidden',
    itemDisabled: 'item-disabled'
};

export default class PsnProductBundle {
    constructor() {
        this.container = document.querySelector(selectors.container);
        this.choicesInstances = [];
        this.init();
    }

    init() {
        if (!this.container) {
            return null;
        }

        this.formElement = this.container.querySelector(selectors.form);

        if (!this.formElement) {
            return null;
        }

        try {
            this.data = JSON.parse(this.formElement.querySelector(selectors.JSON).innerHTML);
        } catch (error) {
            this.data = null;
        }

        if (!this.data) {
            return null;
        }

        this.initElements();

        this.attachListeners();

        // Sync size options based on selected color BEFORE initializing Choices.js
        // This ensures the correct sizes are shown for each color on initial load
        this.syncInitialSizeOptions();

        this.initChoices();
    }

    /**
     * Syncs size options for each bundle item based on its selected color.
     * This ensures the correct sizes are shown for each color on initial load.
     */
    syncInitialSizeOptions() {
        const itemElements = Array.from(this.formElement.querySelectorAll(selectors.item));

        itemElements.forEach(itemElement => {
            const colorSelect = itemElement.querySelector(selectors.itemColorOption);
            if (!colorSelect) return;

            const color = colorSelect.value;
            const product = this.data.products.find(p => p.color === color);
            if (!product) return;

            const itemOptionsElement = itemElement.querySelector(selectors.itemOptions);
            if (!itemOptionsElement) return;

            const itemIndex = itemElement.dataset.bundleProductItem;

            // Rebuild the size options HTML using the correct product data
            const optionsHTML = product.options_with_values.map(productOption => {
                const optionId = `product-option-${itemIndex}-${productOption.position}`;

                // Try to preserve the previously selected value if it exists in the new options
                const existingOption = itemElement.querySelector(`[data-bundle-product-item-option="${productOption.position}"]`);
                const existingValue = existingOption ? existingOption.value : null;

                return `
                    <div class="product-bundle__option-wrapper">
                        <label for="${optionId}" class="product-bundle__option-label">
                            <span class="ps-relative">
                                ${productOption.name}
                                <span class="product-bundle__option-counter ps-absolute variant-count ps-font-light ps-text-[7px] lg:ps-text-[8px] ps-leading-3">${productOption.values.length}</span>
                            </span>
                        </label>

                        <select
                            name="${optionId}"
                            id="${optionId}"
                            data-bundle-product-item-option="${productOption.position}"
                            data-bundle-product-item-select
                        >
                            ${productOption.values.map(value => {
                                const isSelected = existingValue === value;
                                const isDisabled = product.variants.find(variant => {
                                    return variant.options[productOption.position - 1] === value;
                                })?.available === false;
                                
                                return `
                                    <option
                                        value="${value}"
                                        ${isSelected ? 'selected="selected"' : ''}
                                        ${isDisabled ? 'disabled="disabled"' : ''}
                                    >
                                        ${value}
                                    </option>
                                `;
                            }).join('')}
                        </select>
                    </div>
                `;
            }).join('');

            itemOptionsElement.innerHTML = optionsHTML;
        });
    }

    initElements() {
        this.submitElement = this.formElement.querySelector(selectors.submit);
    }

    get currentSelectedCount() {
        const selectedOption = this.container.querySelector(`${selectors.option}:checked`);

        if (!selectedOption) {
            return 1;
        }

        const value = parseInt(selectedOption.value);

        if (isNaN(value)) {
            return 1;
        }

        return value;
    }

    get activeItemElements() {
        return Array.from(this.formElement.querySelectorAll(`${selectors.item}:not(.${modifiers.hidden})`));
    }

    get currentSelectedItemsQuantity() {
        const activeItemElements = this.activeItemElements;

        return activeItemElements.reduce((result, activeItemElement) => {
           const quantityElement = activeItemElement.querySelector(selectors.itemQuantity);

           if (!quantityElement) {
               return result;
           }

           return result + parseInt(quantityElement.value, 10)
        }, 0);
    }

    destroyChoices(ignoreColor = false) {
        this.choicesInstances.forEach(choices => {

            if (ignoreColor && choices.passedElement.element.matches(selectors.itemColorOption)) {
                return null;
            }
            choices.destroy();
        });
    }

    initChoices(ignoreColor = false) {

        const choicesElements = Array.from(this.formElement.querySelectorAll(selectors.itemSelect));

        this.choicesInstances = choicesElements.map(choicesElement => {
            if (ignoreColor && choicesElement.matches(selectors.itemColorOption)) {
                return null;
            }

            const choices = Array.from(choicesElement.querySelectorAll('option')).map(option => {
                const optionColor = option.dataset.bundleProductItemColor;
                const title = option.innerText;

                const label = optionColor ? `<span class="product-bundle__color-swatch" style="background: ${optionColor};"></span>${title}` : title;

                return {
                    value: option.value,
                    label,
                    selected: option.value === choicesElement.value,
                    customProperties: {
                        disabled: option.disabled,
                    }
                };
            });

            if (!choices.length) {
                return null;
            }

            choicesElement.innerHTML = '';

            const choicesInstance = new Choices(choicesElement, {
                choices,
                searchEnabled: false,
                shouldSort: false,
                allowHTML: true,
                itemSelectText: '',

                    callbackOnCreateTemplates: function(strToEl, escapeForTemplate, getClassNames) {
                        return {
                            choice: ({ classNames }, data) => {
                                return strToEl(`
                                  <div class="${data.customProperties.disabled ? 'option-disabled' : '' } ${getClassNames(classNames.item).join(' ')} ${getClassNames(classNames.itemChoice).join(' ')} ${
                                                            getClassNames(data.disabled ? classNames.itemDisabled : classNames.itemSelectable).join(' ')
                                                        }" data-select-text="${this.config.itemSelectText}" data-choice ${
                                                            data.disabled
                                                                ? 'data-choice-disabled aria-disabled="true"'
                                                                : 'data-choice-selectable'
                                                        } data-id="${data.id}" data-value="${escapeForTemplate(data.value)}" ${
                                                            data.groupId > 0 ? 'role="treeitem"' : 'role="option"'
                                                        }>
                                    ${data.label}
                                  </div>
                                `);
                            },
                        };
                    },
            });
            //choicesInstance.setChoices(choices, 'value', 'label', true);
            //choicesInstance.setChoiceByValue(choices[0].value);
            return choicesInstance;
        }).filter(Boolean);
    }

    onOptionChange(e) {
        e.preventDefault();

        this.changeTotalPrice(e.target)

        const itemElements = Array.from(this.formElement.querySelectorAll(selectors.item));

        const currentSelectedCount = this.currentSelectedCount;

        const quantityBulkActions = [];

        itemElements.forEach((itemElement, index) => {
            const position = itemElement.dataset.bundleProductItem || index + 1;


            itemElement.classList.toggle(modifiers.hidden, position > currentSelectedCount);

            quantityBulkActions.push({
                item: itemElement.querySelector(selectors.itemQuantity),
                value: 1
            });
        });

        this.setItemQuantity(quantityBulkActions);

        const firstQuantityWrapper = itemElements[0].querySelector(selectors.itemQuantityWrapper);

        if (firstQuantityWrapper) {
            firstQuantityWrapper.classList.toggle(modifiers.hidden, currentSelectedCount === 1)
        }

        this.update();
    }

    changeTotalPrice(target) {
        const price = target.getAttribute('data-total-price');

        const buttonPrice = this.submitElement.querySelector(selectors.totalPrice)

        if (!buttonPrice) {
            return
        }

        buttonPrice.innerText = price;
    }

    onItemColorOptionChange(e, target) {
        e.preventDefault();

        const color = target.value;

        const itemElement = target.closest(selectors.item);

        if (!itemElement || !color) {
            return null;
        }

        const itemIndex = itemElement.dataset.bundleProductItem;
        const product = this.data.products.find(product => product.color === color);

        if (!product || !itemIndex) {
            return null;
        }

        const itemOptionsElement = itemElement.querySelector(selectors.itemOptions);

        if (!itemOptionsElement) {
            return null;
        }

        const optionsHTML = product.options_with_values.map(productOption => {
            const optionId = `product-option-${itemIndex}-${productOption.position}`;

            const existingOption = itemElement.querySelector(`[data-bundle-product-item-option="${productOption.position}"]`)

            return `
                <div class="product-bundle__option-wrapper">
                    <label for="${optionId}" class="product-bundle__option-label">
                        
                        <span class="ps-relative">
                            ${productOption.name}
                            <span class="product-bundle__option-counter ps-absolute variant-count ps-font-light ps-text-[7px] lg:ps-text-[8px] ps-leading-3">${productOption.values.length}</span>
                        </span>
                    </label>

                    <select
                        name="${optionId}"
                        id="${optionId}"
                        data-bundle-product-item-option="${productOption.position}"
                        data-bundle-product-item-select
                    >
                        ${productOption.values.map(value => {
                            const isSelected = existingOption ? existingOption.value === value : false;
                            const isDisabled = product.variants.find(variant => {
                                return variant.options[productOption.position - 1] === value;
                            })?.available === false;
                            
                            return `
                                <option
                                    value="${value}"
                                    ${isSelected ? 'selected="selected"' : ''}
                                    ${isDisabled ? 'disabled="disabled"' : ''}
                                >
                                    ${value}
                                </option>
                            `;
                        }).join('')}
                    </select>
                </div>
            `;
        }).join('');

        this.destroyChoices(true);

        itemOptionsElement.innerHTML = optionsHTML;

        this.initChoices(true);

        this.update();
    }

    onItemOptionChange(e, target) {
        e.preventDefault();

        this.update();
    }

    getIsAllVariantsAvailable(items) {
        return items.every(item => {
            if (item.quantity === 0) {
                return true;
            }

            return item.variant?.available;
        });
    }

    getActiveItemElementsWithVariants() {
        return this.activeItemElements.map(itemElement => {
            const index = itemElement.dataset.bundleProductItem;

            const quantityElement = itemElement.querySelector(selectors.itemQuantity);
            const quantity = parseInt(quantityElement.value, 10);

            return {
                index,
                itemElement,
                quantity: quantity,
                variant: this.getVariant(itemElement)
            }
        });
    }

    update() {
        const items = this.getActiveItemElementsWithVariants();

        const isAllVariantsAvailable = this.getIsAllVariantsAvailable(items);

        this.updateItemsState(items);
        this.updateItemsImages(items);
        this.updateButton(isAllVariantsAvailable);
    }

    updateItemsState(items) {
        items.forEach(item => {
            let shouldBeDisabled = false;

            if (!item.variant?.available) {
                shouldBeDisabled = true;
            } else if (item.quantity === 0 && this.currentSelectedItemsQuantity === this.currentSelectedCount) {
                shouldBeDisabled = true;
            }

            item.itemElement.classList.toggle(modifiers.itemDisabled, shouldBeDisabled);
        });
    }

    updateItemsImages(items) {
        items.forEach(item => {
            const imageElement = item.itemElement.querySelector(selectors.itemImage);
            const featuredImage = item.variant ? item.variant.featured_image : null;

            if (!imageElement || !featuredImage) {
                return null;
            }

            const src = appendQueryToShopifyImageUrl(featuredImage, '120x');

            if (imageElement.src === src) {
                return null;
            }

            imageElement.setAttribute('src', src);
            imageElement.setAttribute('srcset', `${src} 1x, ${appendQueryToShopifyImageUrl(featuredImage, '240x')} 2x`);
        });
    }

    updateButton(isAllVariantsAvailable) {
        if (!this.submitElement) {
            return null;
        }

        if (isAllVariantsAvailable && this.currentSelectedItemsQuantity === this.currentSelectedCount) {
            this.submitElement.removeAttribute('disabled');
        } else {
            this.submitElement.setAttribute('disabled', 'disabled');
        }
    }

    getVariant(itemElement) {
        const color = itemElement.querySelector(selectors.itemColorOption)?.value;
        if (!color) {
            return null;
        }

        const product = this.data.products.find(product => product.color === color);

        if (!product) {
            return null;
        }
        const selectedOptions = Array.from(itemElement.querySelectorAll(selectors.itemOption)).map(select => {
            return select.value;
        });

        const variant = product.variants.find(variant => {
            return variant.options.every((option, index) => option === selectedOptions[index]);
        });
        if(!variant) {
            return null;
        }

        if (!variant.featured_image) {
            variant.featured_image = product.featured_image;
        }

        return variant;
    }

    async onSubmit(e) {
        e.preventDefault();

        const items = this.getActiveItemElementsWithVariants().reduce((result, activeItem) => {
            if (activeItem.quantity === 0) {
                return result;
            }

            return [
                ...result,
                {
                    id: activeItem.variant.id,
                    quantity: activeItem.quantity
                }
            ]
        }, []);

        const drawer = document.querySelector('psn-cart-drawer');
        const sectionId = drawer?.dataset.sectionId || null;
        const body = { items };
        if (sectionId) {
            body.sections = sectionId;
            body.sections_url = window.location.pathname;
        }

        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            const data = await response.json();

            if (data.status) {
                return;
            }

            if (typeof window.upcartRegisterAddToCart === 'function') {
                window.upcartRegisterAddToCart();
            }

            // /cart/add.js returns the line item (qty added), not the cart.
            // Resolve item_count from the cart object every time.
            const cartData = data.total_price ? data : await fetch('/cart.js').then((r) => r.json()).catch(() => null);
            const itemCount = cartData?.item_count ?? 0;
            const sectionHtml = data.sections?.[sectionId];

            if (sectionHtml && drawer) {
                document.dispatchEvent(
                    new CustomEvent('cart:update', {
                        bubbles: true,
                        detail: {
                            cart: cartData,
                            itemCount,
                            sections: data.sections,
                            source: 'psn-product-bundle'
                        }
                    })
                );
            }

            document.dispatchEvent(
                new CustomEvent('cart:add', {
                    bubbles: true,
                    detail: { itemCount, cart: cartData }
                })
            );
            document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
        } catch (error) {
            console.error('Error:', error);
        }
    }

    onItemQuantityButtonClick(e, target) {
        e.preventDefault();

        const quantityWrapperElement = target.closest(selectors.itemQuantityWrapper);

        if (!quantityWrapperElement) {
            return null;
        }

        const quantityElement = quantityWrapperElement.querySelector(selectors.itemQuantity);

        if (!quantityElement) {
            return null;
        }

        const action = target.dataset.bundleProductItemQuantityButton;

        if (action === 'increment') {
            this.setItemQuantity([{
                item: quantityElement,
                value: parseInt(quantityElement.value, 10) + 1
            }]);
        } else if (action === 'decrement') {
            const newValue = parseInt(quantityElement.value, 10) - 1;
            this.setItemQuantity([{
                item: quantityElement,
                value: newValue > -1 ? newValue : 0
            }]);
        }

        this.update();
    }

    updateQuantityLabel(itemElement) {
        const quantityElement = itemElement.querySelector('[data-bundle-product-item-quantity]');
        const quantityLabelElement = itemElement.querySelector('[data-bundle-product-item-quantity-label]');

        if (!quantityElement || !quantityLabelElement) {
            return;
        }

        const quantity = parseInt(quantityElement.value, 10);

        if (quantity === 0) {
            quantityLabelElement.innerText = '';
        } else if (quantity === 1) {
            quantityLabelElement.innerText = '1 Pair';
        } else {
            quantityLabelElement.innerText = `${quantity} Pairs`;
        }
    }

    setItemQuantity(bulkActions = []) {
        bulkActions.forEach(({ item, value }) => {
            item.value = value;
            const itemElement = item.closest(selectors.item);
            this.updateQuantityLabel(itemElement);
        });

        const activeItemElements = this.activeItemElements;

        const currentSelectedItemsQuantity = this.currentSelectedItemsQuantity;
        const currentSelectedCount = this.currentSelectedCount;

        const quantityWrapperElements = activeItemElements.map(activeItemElement => {
            return activeItemElement.querySelector(selectors.itemQuantityWrapper);
        }).filter(Boolean);

        quantityWrapperElements.forEach(quantityWrapperElement => {
            const quantityElement = quantityWrapperElement.querySelector(selectors.itemQuantity);

            if (!quantityElement) {
                return null;
            }

            const quantity = parseInt(quantityElement.value, 10);

            const quantityButtonElements = quantityWrapperElement.querySelectorAll(selectors.itemQuantityButton);

            quantityButtonElements.forEach(quantityButtonElement => {
                const action = quantityButtonElement.dataset.bundleProductItemQuantityButton;

                let shouldBeDisabled = false;

                if (action === 'increment' && currentSelectedItemsQuantity >= currentSelectedCount) {
                    shouldBeDisabled = true;
                } else if (action === 'decrement' && quantity === 0) {
                    shouldBeDisabled = true;
                }

                if (shouldBeDisabled) {
                    quantityButtonElement.setAttribute('disabled', 'disabled');
                } else {
                    quantityButtonElement.removeAttribute('disabled');
                }
            });
        });
    }

    attachListeners() {
        this.formElement.customDelegate('change', selectors.option, this.onOptionChange.bind(this));
        this.formElement.customDelegate('change', selectors.itemColorOption, this.onItemColorOptionChange.bind(this));
        this.formElement.customDelegate('change', selectors.itemOption, this.onItemOptionChange.bind(this));
        this.formElement.customDelegate('click', selectors.itemQuantityButton, this.onItemQuantityButtonClick.bind(this));
        this.formElement.addEventListener('submit', this.onSubmit.bind(this));
    }
}
